import React, { useRef, useState, useEffect, KeyboardEvent } from 'react';

interface PinInputProps {
	onPinInputComplete: (a: string[], b: boolean, c?: boolean) => void;
	isPinError: string;
	clearInput: boolean;
	onInputChange: (err: boolean) => void;
}

const PinInput = ({ onPinInputComplete, isPinError, onInputChange, clearInput }: PinInputProps) => {
	const pinInputs: { [key: string]: string } = {
		pin1: '',
		pin2: '',
		pin3: '',
		pin4: '',
		pin5: '',
		pin6: ''
	};
	const [pinKeys, setPinKeys] = useState(['', '', '', '', '', '']);
	const pinRefs: any = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

	useEffect(() => {
		if (pinRefs[0].current) {
			pinRefs[0].current.focus();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleInputChange = (event: any, index: number) => {
		const { value } = event.target;
		let inputValue = value.slice(-1);
		const reg = /^[a-z0-9]+$/i;

		if (inputValue !== null && inputValue !== undefined && reg.test(value)) {
			inputValue = inputValue.toUpperCase();

			updatePinKeys(inputValue, index);

			if (pinRefs[index + 1] && pinRefs[index + 1].current) {
				pinRefs[index + 1].current.focus();
				pinRefs[index + 1].current.select();
			}
			pinRefs[index].current.value = inputValue;
		} else {
			pinRefs[index].current.value = '';
			updatePinKeys('', index);
		}

		onInputChange(false);
	};

	const onKeyDownHandle = (event: KeyboardEvent, index: number) => {
		// Left arrow key down
		if (event.keyCode === 37 && pinRefs[index - 1] && pinRefs[index - 1].current) {
			setTimeout(() => {
				pinRefs[index - 1].current.focus();
				pinRefs[index - 1].current.select();
			});

			return;
		}

		// Right arrow key down
		if (event.keyCode === 39 && pinRefs[index + 1] && pinRefs[index + 1].current) {
			setTimeout(() => {
				pinRefs[index + 1].current.focus();
				pinRefs[index + 1].current.select();
			});
			return;
		}

		// Backspace key down
		if (index && event.keyCode === 8 && pinRefs[index].current.value.length === 0) {
			if (pinRefs[index - 1] && pinRefs[index - 1].current) {
				pinRefs[index - 1].current.focus();
				pinRefs[index - 1].current.value = '';

				updatePinKeys('', index - 1);
			}
		}

		// Current input value clear
		if (index && event.keyCode === 8 && pinRefs[index].current.value.length === 1) {
			updatePinKeys('', index);
		}
	};

	function updatePinKeys(inputValue: any, index: number) {
		const newPinKeys = [...pinKeys];
		newPinKeys[index] = inputValue;
		setPinKeys(newPinKeys);

		if (newPinKeys.join('').length === pinKeys.length) {
			onPinInputComplete(newPinKeys, true, true);
		} else {
			onPinInputComplete(newPinKeys, false);
		}
	}

	useEffect(() => {
		if (isPinError) {
			pinRefs[0].current.focus();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isPinError]);

	useEffect(() => {
		if (clearInput) {
			pinRefs.map((pinValue: any) => {
				return (pinValue.current.value = '');
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clearInput]);

	return (
		<div className={isPinError ? 'pin-info valid-error' : 'pin-info'}>
			{Object.keys(pinInputs).map((inputKey, index) => {
				return (
					<div key={index} className="inputdiv">
						<input
							aria-required="true"
							id={'pin' + index}
							aria-describedby="validpinError"
							className="form-control pin-input"
							autoComplete="off"
							ref={pinRefs[index]}
							aria-label={inputKey}
							name={inputKey}
							onKeyDown={(e) => onKeyDownHandle(e, index)}
							onChange={(e) => handleInputChange(e, index)}
						/>
					</div>
				);
			})}

			{isPinError && (
				<span className="error-info" id="validpinError" role="alert">
					{isPinError === 'DEVICE_NAME_MUST_BE_UNIQUE'
						? 'A device with this name exists on this account, please change the device name using your remote by pressing Menu. Navigate to System and select “TV Name”'
						: 'Unable to match code. Try again.'}
				</span>
			)}
		</div>
	);
};

export default React.memo(PinInput);
